import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material"
import { format } from "date-fns"
import React, { useMemo } from "react"
import EventItem from "./EventItem"
import FilterItem from "./FilterItem"

const MoreEventsModal = ({ isOpen = false, onClose = () => {}, events = [], filters = {} }) => {
  // Early return if modal shouldn't be shown

  // Handle empty events array
  const hasEvents = Array.isArray(events) && events.length > 0

  // Sort events by start time (memoized)
  const sortedEvents = useMemo(() => {
    if (!hasEvents) return []

    return [...events].sort((a, b) => {
      return new Date(a.start) - new Date(b.start)
    })
  }, [events, hasEvents])

  // Get formatted date from first event or filters
  const formattedDay = useMemo(() => {
    try {
      if (filters.formattedDay) return filters.formattedDay

      if (hasEvents) {
        const firstEvent = events[0]
        const date = firstEvent?.start ? new Date(firstEvent.start) : new Date()
        return format(date, "EEEE, MMMM d, yyyy")
      }

      return format(new Date(), "EEEE, MMMM d, yyyy")
    } catch (error) {
      return "Event Details"
    }
  }, [events, hasEvents, filters.formattedDay])

  if (!isOpen) return null

  // Extract filter information from props
  const { school, tutor, group } = filters

  // Event click handler
  const handleEventClick = event => {
    if (event.link) window.open(event.link, "_blank")
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          maxWidth: "500px",
          overflow: "hidden",
        },
        elevation: 3,
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          bgcolor: "rgba(67, 57, 242, 0.04)",
          borderBottom: "1px solid rgba(67, 57, 242, 0.15)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight="600" fontFamily="Poppins, Helvetica">
              {formattedDay}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(67, 57, 242, 0.08)",
                color: "primary.main",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ p: 4, pt: 3, pb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: "'Poppins', Helvetica",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: "24px",
            color: "rgba(18, 18, 18, 1)",
            mb: 1,
          }}
        >
          Filters applied:
        </Typography>
        <Stack spacing={1}>
          <FilterItem label="Tutor" value={tutor?.title || "(None selected)"} />
          <FilterItem label="School" value={school?.title || "(None selected)"} />
          <FilterItem label="Group" value={group?.title || "(None selected)"} />
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 1, pb: 2, maxHeight: "80vh", overflow: "auto" }}>
        {hasEvents ? (
          sortedEvents.map((event, index) => (
            <EventItem key={`${event.id || index}`} event={event} onClick={handleEventClick} />
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CalendarTodayIcon fontSize="large" color="disabled" />
            <Typography variant="body1">No events to display</Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(MoreEventsModal)
